import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Grid } from "@mui/material";

import ReportePagina from "../../components/common/ReportePagina";
import FiltroFilial from "../../components/common/FiltroFilial";
import { useFiltroFilial } from "../../hooks/useFiltroFilial";
import { getsSesionPlanillaView } from "../../services/sesionService";
import { getsListNaturalPersonByTypeAndName } from "../../services/personaService";
import { getsPeriodoView } from "../../services/periodoService";
import { Autocomplete, FormControl, InputLabel, TextField } from "@mui/material";

// Reporte de Sesiones: horas registradas y validadas por terapeuta. Es la
// lectura de la carga de trabajo; las horas que cuentan son las de las sesiones
// validadas, por eso se separan las dos columnas.
const Sesiones = () => {
    const [idTerapeuta, setIdTerapeuta] = useState(0);
    const [idPeriodo, setIdPeriodo] = useState(0);
    const [listTerapeutas, setListTerapeutas] = useState([]);
    const [listPeriodos, setListPeriodos] = useState([]);
    const [rows, setRows] = useState([]);
    const [cargando, setCargando] = useState(true);
    // El filtro de filial: el usuario con una filial solo ve esa, y ademas
    // lista los terapeutas que tienen terapias activas en la filial.
    const { filiales, idFilial, setIdFilial, terapeutas, bloqueado } = useFiltroFilial();

    const fetchTerapeutas = useCallback(async () => {
        const data = await getsListNaturalPersonByTypeAndName(25, "", true)
            .catch((err) => { console.log("Err: ", err); });

        setListTerapeutas(data || []);
    }, [])

    const fetchPeriodos = useCallback(async () => {
        const data = await getsPeriodoView({
            idTipoTerapia: 0,
            idEstadoApertura: 87,
            mesesHaciaAtras: 0,
            idEstado: 2,
        }).catch((err) => { console.log("Err: ", err); });

        setListPeriodos(data || []);
    }, [])

    const fetchResults = useCallback(async (terapeuta, periodo) => {
        setCargando(true);

        await getsSesionPlanillaView({ idTerapeuta: terapeuta, idPeriodo: periodo })
            .then((data) => {
                setRows(data || []);
                setCargando(false);
            })
            .catch((err) => {
                setCargando(false);
                console.log("Err: ", err);
            });
    }, [])

    useEffect(() => {
        fetchTerapeutas();
        fetchPeriodos();
        fetchResults(0, 0);
    }, [fetchTerapeutas, fetchPeriodos, fetchResults])

    // Los terapeutas que se ofrecen: los que tienen terapias activas en la
    // filial elegida. Sin filial, todos.
    const terFiltrados = useMemo(() => {
        if (idFilial <= 0) return listTerapeutas;

        const ids = new Set(terapeutas.map((t) => t.id));

        return listTerapeutas.filter((t) => ids.has(t.id));
    }, [listTerapeutas, terapeutas, idFilial]);

    // El resumen llega a nivel de participante (seudonimo). Para el reporte se
    // agrupa por terapeuta: es la lectura que interesa.
    const filas = useMemo(() => {
        const mapa = new Map();

        for (const row of rows) {
            const terapeuta = row.terapeuta || "Sin terapeuta";

            if (!mapa.has(terapeuta)) {
                mapa.set(terapeuta, {
                    terapeuta,
                    periodos: new Set(),
                    sesiones: 0,
                    validadas: 0,
                    minutos: 0,
                });
            }

            const fila = mapa.get(terapeuta);

            if (row.codigoPeriodo) fila.periodos.add(row.codigoPeriodo);

            fila.sesiones += row.numeroSesiones || 0;
            fila.validadas += row.numeroSesionesValidadas || 0;
            fila.minutos += row.minutosSesion || 0;
        }

        return Array.from(mapa.values()).map((fila) => {
            const pendientes = fila.sesiones - fila.validadas;
            const horas = fila.minutos / 60;

            return {
                terapeuta: fila.terapeuta,
                periodos: fila.periodos.size,
                sesiones: fila.sesiones,
                validadas: fila.validadas,
                pendientes,
                minutos: fila.minutos,
                horas: horas.toFixed(2),
                // Porcentaje validado: si esta bajo, hay trabajo sin validar.
                porcentajeValidado: fila.sesiones > 0
                    ? Math.round((fila.validadas / fila.sesiones) * 100) + "%"
                    : "0%",
            };
        }).sort((a, b) => b.horas - a.horas);
    }, [rows]);

    const totSesiones = filas.reduce((suma, f) => suma + f.sesiones, 0);
    const totValidadas = filas.reduce((suma, f) => suma + f.validadas, 0);
    const totMinutos = filas.reduce((suma, f) => suma + f.minutos, 0);
    const totHoras = (totMinutos / 60).toFixed(2);

    const columnas = [
        { campo: "terapeuta", titulo: "Terapeuta", ancho: 28 },
        { campo: "periodos", titulo: "Periodos", ancho: 9, alin: "center" },
        { campo: "sesiones", titulo: "Registradas", ancho: 11, alin: "center" },
        { campo: "validadas", titulo: "Validadas", ancho: 11, alin: "center" },
        { campo: "pendientes", titulo: "Sin validar", ancho: 11, alin: "center" },
        { campo: "minutos", titulo: "Minutos", ancho: 10, alin: "center" },
        { campo: "horas", titulo: "Horas", ancho: 10, alin: "center" },
        { campo: "porcentajeValidado", titulo: "% validado", ancho: 10, alin: "center" },
    ];

    return (
        <ReportePagina
            titulo="Sesiones por Terapeuta"
            subtitulo="Sesiones registradas y validadas, con las horas que acumulan. Las horas que cuentan son las de las sesiones validadas."
            cargando={cargando}
            indicadores={[
                { etiqueta: "Sesiones", valor: String(totSesiones), color: "#5b2d8e" },
                { etiqueta: "Validadas", valor: String(totValidadas), color: "#00796b" },
                { etiqueta: "Sin validar", valor: String(totSesiones - totValidadas), color: "#e07b00" },
                { etiqueta: "Horas", valor: totHoras, color: "#c8102e" },
            ]}
            filtros={
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={3}>
                        <FiltroFilial
                            filiales={filiales}
                            idFilial={idFilial}
                            bloqueado={bloqueado}
                            onChange={setIdFilial}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Autocomplete
                            size="small"
                            options={terFiltrados}
                            getOptionLabel={(opcion) => opcion.descripcion || ""}
                            value={terFiltrados.find((t) => t.id === idTerapeuta) || null}
                            onChange={(event, nuevo) => {
                                const elegido = nuevo || null;
                                setIdTerapeuta(elegido ? elegido.id : 0);
                                fetchResults(elegido ? elegido.id : 0, idPeriodo);
                            }}
                            renderInput={(params) => <TextField {...params} label="Terapeuta" />}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel htmlFor="input-periodoreporte" shrink>Periodo</InputLabel>
                            <select
                                id="input-periodoreporte"
                                value={idPeriodo}
                                onChange={(event) => {
                                    const periodo = parseInt(event.target.value);
                                    setIdPeriodo(periodo);
                                    fetchResults(idTerapeuta, periodo);
                                }}
                                style={{
                                    width: "100%",
                                    height: 40,
                                    padding: "0 14px",
                                    border: "1px solid rgba(0, 0, 0, 0.23)",
                                    borderRadius: 4,
                                    background: "inherit",
                                }}
                            >
                                <option value={0}>Todos</option>
                                {listPeriodos.map((periodo) => (
                                    <option key={periodo.id} value={periodo.id}>
                                        {periodo.codigo || periodo.id}
                                    </option>
                                ))}
                            </select>
                        </FormControl>
                    </Grid>
                </Grid>
            }
            grafico={filas.length ? {
                titulo: "Horas por terapeuta",
                subtitulo: "Horas de sesiones validadas",
                etiquetas: filas.map((f) => f.terapeuta),
                valores: filas.map((f) => Number(f.horas)),
            } : null}
            columnas={columnas}
            filas={filas}
        />
    )
}

export default Sesiones;